import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CompanySettings.css';

const TIMEZONES = [
  { value: 'Europe/Moscow', label: 'Москва (UTC+3)' },
  { value: 'Europe/Kaliningrad', label: 'Калининград (UTC+2)' },
  { value: 'Europe/Samara', label: 'Самара (UTC+4)' },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург (UTC+5)' },
  { value: 'Asia/Omsk', label: 'Омск (UTC+6)' },
  { value: 'Asia/Krasnoyarsk', label: 'Красноярск (UTC+7)' },
  { value: 'Asia/Irkutsk', label: 'Иркутск (UTC+8)' },
  { value: 'Asia/Yakutsk', label: 'Якутск (UTC+9)' },
  { value: 'Asia/Vladivostok', label: 'Владивосток (UTC+10)' },
  { value: 'Asia/Magadan', label: 'Магадан (UTC+11)' },
  { value: 'Asia/Kamchatka', label: 'Камчатка (UTC+12)' },
  { value: 'Europe/Minsk', label: 'Минск (UTC+3)' },
  { value: 'Europe/Kiev', label: 'Киев (UTC+2/3)' },
  { value: 'Asia/Almaty', label: 'Алматы (UTC+5/6)' },
  { value: 'Asia/Tashkent', label: 'Ташкент (UTC+5)' },
];

function CompanySettings({ token, company, onUpdate }) {
  const [formData, setFormData] = useState({
    company_name: '',
    website_url: '',
    ai_prompt: '',
    company_knowledge_base: '',
    timezone: 'Europe/Moscow',
    working_hours: '9:00-21:00',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        company_name: company.company_name || '',
        website_url: company.website_url || '',
        ai_prompt: company.ai_prompt || '',
        company_knowledge_base: company.company_knowledge_base || '',
        timezone: company.timezone || 'Europe/Moscow',
        working_hours: company.working_hours || '9:00-21:00',
      });
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await axios.put(
        `/api/v1/tenants/${company.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage('Настройки успешно сохранены!');
      setIsSuccess(true);
      if (onUpdate) onUpdate(response.data);

      setTimeout(() => {
        setMessage('');
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      setMessage(`Ошибка сохранения: ${error.response?.data?.detail || error.message}`);
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (!company) return;
    setFormData({
      company_name: company.company_name || '',
      website_url: company.website_url || '',
      ai_prompt: company.ai_prompt || '',
      company_knowledge_base: company.company_knowledge_base || '',
      timezone: company.timezone || 'Europe/Moscow',
      working_hours: company.working_hours || '9:00-21:00',
    });
  };

  return (
    <div className="company-settings">
      <div className="settings-header">
        <h2>Настройки компании</h2>
        <div className="company-id">
          ID: <code>{company?.id}</code>
        </div>
      </div>

      {message && (
        <div className={`message ${isSuccess ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-section">
          <h3>Основная информация</h3>
          <div className="form-group">
            <label htmlFor="company_name">Название компании *</label>
            <input
              type="text"
              id="company_name"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              required
              placeholder="Например: ООО 'Рога и Копыта'"
            />
          </div>
          <div className="form-group">
            <label htmlFor="website_url">URL сайта</label>
            <input
              type="url"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              placeholder="https://example.com"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Часы работы</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="timezone">Часовой пояс</label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
              >
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="working_hours">График работы</label>
              <input
                type="text"
                id="working_hours"
                name="working_hours"
                value={formData.working_hours}
                onChange={handleChange}
                placeholder="9:00-21:00"
                pattern="^\d{1,2}:\d{2}-\d{1,2}:\d{2}$"
                title="Формат: ЧЧ:ММ-ЧЧ:ММ (например 9:00-21:00)"
              />
              <small className="hint">Формат: 9:00-21:00</small>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Настройки AI-консультанта</h3>
          <div className="form-group">
            <label htmlFor="ai_prompt">
              Системный промпт для AI *
              <span className="hint">Определяет поведение и стиль общения бота</span>
            </label>
            <textarea
              id="ai_prompt"
              name="ai_prompt"
              value={formData.ai_prompt}
              onChange={handleChange}
              required
              rows={5}
              placeholder="Ты - профессиональный консультант..."
            />
            <div className="char-count">{formData.ai_prompt.length} символов</div>
          </div>

          <div className="form-group">
            <label htmlFor="company_knowledge_base">
              База знаний о компании
              <span className="hint">Информация, которую должен знать бот</span>
            </label>
            <textarea
              id="company_knowledge_base"
              name="company_knowledge_base"
              value={formData.company_knowledge_base}
              onChange={handleChange}
              rows={8}
              placeholder="Основные услуги: ..."
            />
            <div className="char-count">{formData.company_knowledge_base.length} символов</div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleReset} className="btn btn-secondary" disabled={loading}>
            Сбросить изменения
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Сохранение...' : 'Сохранить изменения'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CompanySettings;